/**
 * The year-end export.
 *
 * **This screen is careful about one thing above all: it is not a tax document
 * and must never read like one.** AEGIS reconstructs trades from broker fills,
 * so its numbers are a record of transactions. The broker's 1099-B is
 * authoritative, the 60/40 split shown here is the general Section 1256
 * treatment rather than a determination about this user, and none of it is
 * advice. The disclaimer comes from the API and travels into the CSV as well,
 * because the CSV is the part that gets forwarded to an accountant.
 *
 * Gated on Trader. A free user sees what it is and what it costs, not a
 * disabled button with no explanation.
 */
import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, ApiError, fmtUsd, type Account } from '../lib/api.js';
import { url } from '../lib/runtime.js';
import { EmptyState } from '../components/EmptyState.js';
import { Link } from '../lib/router.js';

interface TaxSummary {
  year: number;
  accountId: string;
  tradeCount: number;
  realizedPnl: string;
  yearEndMarkToMarket: string;
  totalSection1256: string;
  longTermPortion: string;
  shortTermPortion: string;
  commissions: string;
  fees: string;
  disclaimer: string;
}

export function TaxScreen({
  accounts, accountId, onAccount,
}: {
  accounts: Account[];
  accountId: string | null;
  onAccount(id: string): void;
}): JSX.Element {
  const thisYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(thisYear - 1);
  const [summary, setSummary] = useState<TaxSummary | null>(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true); setError(null); setLocked(false);
    try {
      setSummary(await api.tax(accountId, year) as unknown as TaxSummary);
    } catch (e) {
      setSummary(null);
      // 402 is not an error the user did anything about — it is the paywall,
      // and it deserves an explanation rather than a red bar.
      if (e instanceof ApiError && e.status === 402) setLocked(true);
      else setError(e instanceof ApiError ? e.message : 'Could not load the summary.');
    } finally {
      setLoading(false);
    }
  }, [accountId, year]);

  useEffect(() => { void load(); }, [load]);

  if (accounts.length === 0) {
    return (
      <div className="scroll-area">
        <h2>Tax export</h2>
        <p className="panel-body">
          Nothing to export yet — this is built from your broker's fills.{' '}
          <Link to="/settings/broker">Connect a broker</Link>.
        </p>
      </div>
    );
  }

  const years = Array.from({ length: 5 }, (_, i) => thisYear - i);

  return (
    <div className="scroll-area">
      <div className="section-head">
        <h2>Tax export</h2>
        <div className="account-picker">
          {accounts.map((a) => (
            <button
              key={a.id}
              className={`chip-btn compact ${accountId === a.id ? 'on' : ''}`}
              onClick={() => onAccount(a.id)}
            >
              {a.nickname}
            </button>
          ))}
          <select
            className="input compact-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            aria-label="Tax year"
          >
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="form-error" role="alert">{error}</div>}

      {locked && (
        <EmptyState
          icon="receipt"
          title="Part of Trader"
          action={<Link to="/pricing" className="btn btn-primary compact">See plans</Link>}
        >
          <p>
            The year-end summary and the transaction CSV your accountant will ask for
            are included from the Trader plan up.
          </p>
        </EmptyState>
      )}

      {loading && <p className="dim">Loading…</p>}

      {summary && (
        <>
          <section className="settings-block">
            <h3 className="panel-h">{summary.year} · {summary.tradeCount} closed trades</h3>
            <dl className="kv">
              <dt>Realised P&amp;L</dt>
              <dd className="mono">{fmtUsd(summary.realizedPnl)}</dd>
              <dt>Year-end mark to market</dt>
              <dd className="mono">{fmtUsd(summary.yearEndMarkToMarket)}</dd>
              <dt>Total (Section 1256)</dt>
              <dd className="mono">{fmtUsd(summary.totalSection1256)}</dd>
              <dt>60% long-term portion</dt>
              <dd className="mono">{fmtUsd(summary.longTermPortion)}</dd>
              <dt>40% short-term portion</dt>
              <dd className="mono">{fmtUsd(summary.shortTermPortion)}</dd>
              <dt>Commissions</dt>
              <dd className="mono">{fmtUsd(summary.commissions)}</dd>
              <dt>Fees</dt>
              <dd className="mono">{fmtUsd(summary.fees)}</dd>
            </dl>

            <div className="settings-links">
              {/*
                A real link with `download`, not a fetch-and-blob. The browser
                streams it, the filename comes from the server's
                content-disposition, and it works with the session cookie the
                same way every other request does.
              */}
              <a
                className="btn btn-primary"
                href={url(`/v1/accounts/${encodeURIComponent(summary.accountId)}/tax?year=${summary.year}&format=csv`)}
                download
              >
                Download transactions (CSV)
              </a>
            </div>
            <p className="field-hint">
              One row per closed trade, with entry, exit, commissions, fees and net P&amp;L.
              The disclaimer below travels in the file, because the file is the part that
              gets forwarded.
            </p>
          </section>

          <section className="settings-block">
            <h3 className="panel-h">READ THIS FIRST</h3>
            <p className="panel-body">{summary.disclaimer}</p>
            <p className="field-hint">
              These figures are reconstructed from the fills your broker reported. If they
              disagree with your 1099-B, the 1099-B is the one the IRS has.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
